/**
 * FracSight 甜点评价 — 统一参数字典与储层评价模板（单一数据源）。
 *
 * 职责：
 * 1. 集中维护 7 类储层类型与中文名称（禁止在组件中重复硬编码）。
 * 2. 集中维护统一参数字典，各储层模板仅引用参数代码。
 * 3. 提供 DataHub 匹配别名、重复加权互斥组、替代/派生参数声明。
 * 4. 将储层模板转换为页面可用的 SweetSpotParameterConfig（默认等权）。
 *
 * 设计原则：模板与算法解耦、算法与 UI 解耦；旧版无储层类型数据时回退旧版默认行为。
 */
import type {
  IntervalScoringConfig, ParameterRequirement, ReservoirQualityTemplate, ReservoirType,
  ScoreDirection, SweetSpotParameter, SweetSpotParameterConfig,
  SweetSpotParameterDefinition,
} from './types';

/** 模板版本：随定义变更递增，用于版本追溯。 */
export const RESERVOIR_TEMPLATE_VERSION = '2026-08-06.v1';

/** 系统预设权重说明：模板内未单独给定权重时采用组内等权。 */
export const DEFAULT_EQUAL_WEIGHT_NOTE = '当前采用系统预设权重；模板未单独给定时按组内等权。';

/** 相对评价提示：未加载区块标定阈值时显示。 */
export const RELATIVE_SCORING_NOTE = '当前结果采用相对评价，尚未加载区块标定阈值。';

/** 储层类型中文名称集中维护。 */
export const RESERVOIR_TYPE_LABELS: Record<ReservoirType, string> = {
  'shale-gas': '页岩气',
  'shale-oil': '页岩油',
  'tight-sandstone-gas': '致密砂岩气',
  'tight-sandstone-oil': '致密砂岩油',
  'tight-conglomerate': '致密砂砾岩/砾岩',
  'coalbed-gas': '煤层气/深层煤岩气',
  custom: '自定义储层',
};

export const RESERVOIR_TYPES: ReservoirType[] = [
  'shale-gas', 'shale-oil', 'tight-sandstone-gas', 'tight-sandstone-oil',
  'tight-conglomerate', 'coalbed-gas', 'custom',
];

export function reservoirTypeLabel(type: ReservoirType): string {
  return RESERVOIR_TYPE_LABELS[type] ?? type;
}

/**
 * CQ 术语说明（界面必须显示）：CQ 用于评价目标层段的起裂、裂缝扩展、
 * 缝网形成及导流保持能力，不等同于固井质量、射孔施工质量或现场作业质量。
 */
export const CQ_TERM_DESCRIPTION = 'CQ 用于评价目标层段的起裂、裂缝扩展、缝网形成及导流保持能力，不等同于固井质量、射孔施工质量或现场作业质量。';

/** 重复加权互斥组定义。 */
export const MUTUAL_EXCLUSION_GROUPS = {
  /** 脆性信息互斥：基础参数组合 vs 综合脆性指数。 */
  BRITTLENESS_INPUT_GROUP: {
    key: 'BRITTLENESS_INPUT_GROUP',
    label: '脆性信息',
    message: '当前配置可能重复计算脆性信息。请选择综合脆性指数，或选择其基础参数组合（杨氏模量＋泊松比＋脆性矿物含量）。',
    basicMode: ['YOUNG_MODULUS', 'POISSON_RATIO', 'BRITTLE_MINERAL'],
    compositeMode: ['BI'],
  },
  /** 页岩油 OSI 互斥：S1＋TOC 或 OSI，二选一。 */
  OSI_GROUP: {
    key: 'OSI_GROUP',
    label: 'OSI 含油指数',
    message: 'OSI 为派生参数（OSI = S1 / TOC）。S1＋TOC 与 OSI 只能选择一种，不得同时作为独立高权重参数。',
    basicMode: ['S1', 'TOC'],
    compositeMode: ['OSI'],
  },
  /** 饱和度互斥：简单油水/气水系统中不得同时高权重使用。 */
  SATURATION_GROUP: {
    key: 'SATURATION_GROUP',
    label: '饱和度',
    message: 'SO/SG 与 SW 不得同时作为独立高权重参数，避免饱和度信息重复加权。',
    basicMode: ['SO', 'SG'],
    compositeMode: ['SW'],
  },
} as const;

export type MutualExclusionKey = keyof typeof MUTUAL_EXCLUSION_GROUPS;

/** DataHub 匹配别名（结合项目现有字段命名，不覆盖已有规范）。 */
export const PARAMETER_ALIASES: Record<string, string[]> = {
  PHIE: ['PHIE', 'POR', 'POROSITY', '有效孔隙度', '孔隙度'],
  PERM: ['PERM', 'K', 'PERMEABILITY', '渗透率'],
  SG: ['SG', 'SWG', '含气饱和度'],
  SO: ['SO', 'SWO', '含油饱和度'],
  SW: ['SW', 'SWT', '含水饱和度'],
  SO_OR_SG: ['SG', 'SO', 'SWG', 'SWO', '含油气饱和度'],
  TOC: ['TOC', '总有机碳'],
  S1: ['S1', 'S1FREE', '游离烃'],
  PRESSURE_COEF: ['PRESSURE_COEF', '压力系数', 'PRES'],
  NET_PAY: ['NET_PAY', 'H_EFF', '有效厚度'],
  MOVABLE_FLUID: ['MOVABLE_FLUID', 'S_MOV', '可动流体'],
  GAS_CONTENT: ['GAS_CONTENT', 'GAS', '总含气量', '含气量'],
  CLEAT_PERM: ['CLEAT_PERM', '割理渗透率'],
  COAL_THICKNESS: ['COAL_THICKNESS', '煤层厚度'],
  DESORPTION_PRESSURE_RATIO: ['DESORPTION_PRESSURE_RATIO', '解吸压力比'],
  LANGMUIR_VOLUME: ['LANGMUIR_VOLUME', 'VL', 'LANGMUIR'],
  FACIES_CONTINUITY: ['FACIES_CONTINUITY', '相带连续性'],
  YOUNG_MODULUS: ['YOUNG_MODULUS', 'YOUNGMODULUS', 'E', 'DYNAMIC_E', '杨氏模量', '弹性模量'],
  POISSON_RATIO: ['POISSON_RATIO', 'POISSON', 'NU', 'PR', '泊松比'],
  HSTRESS_DIFF: ['HSTRESS_DIFF', 'SIGMA_DIFF', '水平主应力差', '应力差'],
  FRACTURE_TOUGHNESS: ['FRACTURE_TOUGHNESS', 'KIC', '断裂韧度'],
  NATURAL_FRACTURE: ['NATURAL_FRACTURE', 'FRACTURE_DENSITY', '天然裂缝', '裂缝密度'],
  BARRIER_STRESS: ['BARRIER_STRESS', '隔层应力差'],
  LAMINATION_INDEX: ['LAMINATION_INDEX', '层理指数', '纹层指数'],
  BARRIER_CONSTRAINT: ['BARRIER_CONSTRAINT', '隔夹层约束'],
  TENSILE_STRENGTH: ['TENSILE_STRENGTH', 'TSTR', '抗拉强度'],
  HETEROGENEITY: ['HETEROGENEITY', '非均质指数'],
  COAL_STRUCTURE: ['COAL_STRUCTURE', '煤体结构'],
  CLEAT_INDEX: ['CLEAT_INDEX', '割理指数'],
  STRESS_SENSITIVITY: ['STRESS_SENSITIVITY', '应力敏感性'],
  SHMIN: ['SHMIN', 'SIGMA_H', 'MINHORIZONTALSTRESS', '最小水平主应力'],
  BI: ['BI', 'BRIT', 'BRITTLENESS', '脆性指数'],
  BRITTLE_MINERAL: ['BRITTLE_MINERAL', 'BRITTLE_MIN', '脆性矿物'],
  CLAY_CONTENT: ['CLAY_CONTENT', 'CLAY', 'VCL', 'CLAYCONTENT', '黏土含量'],
  WATER_SENSITIVITY: ['WATER_SENSITIVITY', '水敏性'],
  PROPPANT_EMBEDMENT: ['PROPPANT_EMBEDMENT', '嵌入风险'],
  PROPPANT_CRUSHING: ['PROPPANT_CRUSHING', '破碎风险'],
  SEVERE_LOSS: ['SEVERE_LOSS', '漏失风险'],
  HEIGHT_CONTROL_RISK: ['HEIGHT_CONTROL_RISK', '缝高失控'],
  SAND_PLUG_RISK: ['SAND_PLUG_RISK', '砂堵风险'],
  COAL_FRAGMENTATION_RISK: ['COAL_FRAGMENTATION_RISK', '煤体破碎'],
};

const definition = (
  input: Omit<SweetSpotParameterDefinition, 'applicableReservoirTypes' | 'allowDataHubMapping' | 'allowManualInput'>
  & Partial<Pick<SweetSpotParameterDefinition, 'applicableReservoirTypes' | 'allowDataHubMapping' | 'allowManualInput'>>,
): SweetSpotParameterDefinition => ({
  allowDataHubMapping: true,
  allowManualInput: true,
  applicableReservoirTypes: RESERVOIR_TYPES,
  ...input,
});

/** 统一参数字典（单一数据源）。 */
export const PARAMETER_DEFINITIONS: Record<string, SweetSpotParameterDefinition> = {
  // ── RQ 储层品质 ──
  TOC: definition({
    code: 'TOC', name: '总有机碳含量', shortName: 'TOC', dimension: 'RQ', unit: '%',
    description: '总有机碳含量，表征有机质富集程度。',
    engineeringMeaning: '有机质是页岩油气生成的物质基础，TOC 过高或过低都可能对应较差的可压/可采条件，采用区间评价。',
    direction: 'interval', requirement: 'recommended', interval: { minAcceptable: 1, optimalMin: 2, optimalMax: 6, maxAcceptable: 12 },
    dataSourceHints: ['测井解释 TOC 曲线', '岩心实测'],
    aliases: PARAMETER_ALIASES.TOC,
  }),
  PHIE: definition({
    code: 'PHIE', name: '有效孔隙度', shortName: 'PHIE', dimension: 'RQ', unit: '%',
    description: '有效孔隙度，表征有效储集空间占比。',
    engineeringMeaning: '孔隙度越高，储集能力越好，是 RQ 的核心正向指标。',
    direction: 'positive', requirement: 'required',
    dataSourceHints: ['密度—中子测井解释', '岩心孔隙度'],
    aliases: PARAMETER_ALIASES.PHIE,
  }),
  PERM: definition({
    code: 'PERM', name: '渗透率', shortName: 'K', dimension: 'RQ', unit: 'mD',
    description: '基质渗透率，表征流体渗流能力。',
    engineeringMeaning: '渗透率跨越多个数量级，评分前需 log10 变换；数值越大越优。',
    direction: 'positive', requirement: 'required',
    dataSourceHints: ['岩心渗透率', '试井解释'],
    aliases: PARAMETER_ALIASES.PERM,
  }),
  SG: definition({
    code: 'SG', name: '含气饱和度', shortName: 'SG', dimension: 'RQ', unit: '%',
    description: '含气饱和度，表征孔隙空间中天然气所占比例。',
    engineeringMeaning: '含气饱和度越高，储量丰度越好。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['解释含气饱和度'],
    aliases: PARAMETER_ALIASES.SG,
  }),
  SO: definition({
    code: 'SO', name: '含油饱和度', shortName: 'SO', dimension: 'RQ', unit: '%',
    description: '含油饱和度，表征孔隙空间中原油所占比例。',
    engineeringMeaning: '含油饱和度越高，储量丰度越好。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['解释含油饱和度'],
    aliases: PARAMETER_ALIASES.SO,
  }),
  SW: definition({
    code: 'SW', name: '含水饱和度', shortName: 'SW', dimension: 'RQ', unit: '%',
    description: '含水饱和度，用于饱和度互斥检查。',
    engineeringMeaning: '在简单油水/气水系统中，SW 与 SO/SG 信息重叠，不得重复加权。',
    direction: 'negative', requirement: 'optional',
    dataSourceHints: ['解释含水饱和度'],
    aliases: PARAMETER_ALIASES.SW,
    mutualExclusionGroup: 'SATURATION_GROUP',
  }),
  SO_OR_SG: definition({
    code: 'SO_OR_SG', name: '含油气饱和度', shortName: 'So/Sg', dimension: 'RQ', unit: '%',
    description: '含油气饱和度，致密砂砾岩采用统一含油气饱和度。',
    engineeringMeaning: '含油气饱和度越高，储量丰度越好。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['解释含油气饱和度'],
    aliases: PARAMETER_ALIASES.SO_OR_SG,
  }),
  PRESSURE_COEF: definition({
    code: 'PRESSURE_COEF', name: '压力系数', shortName: 'PRES', dimension: 'RQ', unit: '无量纲',
    description: '地层压力系数，表征超压程度。',
    engineeringMeaning: '超压有利于含气/含油富集与返排，压力系数越高越优。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['实测地层压力', '解释压力'],
    aliases: PARAMETER_ALIASES.PRESSURE_COEF,
  }),
  NET_PAY: definition({
    code: 'NET_PAY', name: '有效储层厚度', shortName: 'H_eff', dimension: 'RQ', unit: 'm',
    description: '有效储层厚度，表征可动储集规模。',
    engineeringMeaning: '有效厚度越大，单段资源基础越好。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['解释有效厚度'],
    aliases: PARAMETER_ALIASES.NET_PAY,
  }),
  GAS_CONTENT: definition({
    code: 'GAS_CONTENT', name: '总含气量', shortName: 'GAS', dimension: 'RQ', unit: 'm³/t',
    description: '总含气量，表征单位质量岩石所含天然气总量。',
    engineeringMeaning: '含气量越高，资源基础越好；替代：含气饱和度或解释含气性。',
    direction: 'positive', requirement: 'required',
    dataSourceHints: ['等温吸附测试', '测井解释含气量'],
    aliases: PARAMETER_ALIASES.GAS_CONTENT,
  }),
  S1: definition({
    code: 'S1', name: '游离烃含量', shortName: 'S1', dimension: 'RQ', unit: 'mg/g',
    description: '游离烃含量（热解 S1），表征已生成并赋存的轻烃。',
    engineeringMeaning: 'S1 直接表征可动游离油资源；与 TOC 组合可派生 OSI。',
    direction: 'positive', requirement: 'required',
    dataSourceHints: ['岩石热解'],
    aliases: PARAMETER_ALIASES.S1,
  }),
  OSI: definition({
    code: 'OSI', name: '含油饱和指数', shortName: 'OSI', dimension: 'RQ', unit: 'mg/g/%',
    description: 'OSI = S1 / TOC，表征单位有机碳的游离烃丰度。',
    engineeringMeaning: 'OSI 为派生参数；S1＋TOC 与 OSI 只能选择一种，不得重复加权。',
    direction: 'positive', requirement: 'recommended', derived: true, derivedFrom: ['S1', 'TOC'],
    mutualExclusionGroup: 'OSI_GROUP',
    dataSourceHints: ['由 S1 与 TOC 派生'],
  }),
  MOVABLE_FLUID: definition({
    code: 'MOVABLE_FLUID', name: '可动流体饱和度', shortName: 'MOV', dimension: 'RQ', unit: '%',
    description: '可动流体饱和度，表征可流动孔隙流体占比。',
    engineeringMeaning: '可动流体越高，动用程度越好；替代：NMR 可动流体饱和度或流动带指数。',
    direction: 'positive', requirement: 'recommended', alternativeGroup: 'MOVABLE_FLUID_GROUP',
    dataSourceHints: ['NMR 解释', '流动带指数'],
    aliases: PARAMETER_ALIASES.MOVABLE_FLUID,
  }),
  CLEAT_PERM: definition({
    code: 'CLEAT_PERM', name: '割理渗透率', shortName: 'CLEAT_K', dimension: 'RQ', unit: 'mD',
    description: '割理渗透率，表征煤层割理系统的渗流能力。',
    engineeringMeaning: '煤层气主要通过割理系统产出，割理渗透率越高越优。',
    direction: 'positive', requirement: 'required',
    dataSourceHints: ['试井解释', '岩心割理分析'],
    aliases: PARAMETER_ALIASES.CLEAT_PERM,
  }),
  COAL_THICKNESS: definition({
    code: 'COAL_THICKNESS', name: '煤层有效厚度', shortName: 'H_coal', dimension: 'RQ', unit: 'm',
    description: '煤层有效厚度，表征可动煤层气资源规模。',
    engineeringMeaning: '煤层越厚，单段资源基础越好。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['煤层解释'],
    aliases: PARAMETER_ALIASES.COAL_THICKNESS,
  }),
  DESORPTION_PRESSURE_RATIO: definition({
    code: 'DESORPTION_PRESSURE_RATIO', name: '解吸压力比', shortName: 'Pd/P', dimension: 'RQ', unit: '无量纲',
    description: '解吸压力与地层压力之比，表征煤层气解吸潜力。',
    engineeringMeaning: '解吸压力比越高，越利于早期解吸产出。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['等温吸附 + 压力测试'],
    aliases: PARAMETER_ALIASES.DESORPTION_PRESSURE_RATIO,
  }),
  LANGMUIR_VOLUME: definition({
    code: 'LANGMUIR_VOLUME', name: 'Langmuir 体积', shortName: 'VL', dimension: 'RQ', unit: 'm³/t',
    description: 'Langmuir 体积，表征最大吸附能力。',
    engineeringMeaning: 'Langmuir 体积越高，煤层吸附潜力越大。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['等温吸附测试'],
    aliases: PARAMETER_ALIASES.LANGMUIR_VOLUME,
  }),
  FACIES_CONTINUITY: definition({
    code: 'FACIES_CONTINUITY', name: '优质相带连续性', shortName: 'FACIES', dimension: 'RQ', unit: '无量纲',
    description: '优质相带连续性，表征砾岩/砂岩优质储层横向连续性。',
    engineeringMeaning: '相带连续性好，甜点段更易整体压裂改造。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['沉积相解释'],
    aliases: PARAMETER_ALIASES.FACIES_CONTINUITY,
  }),

  // ── CQ 完井品质（压裂可改造性） ──
  YOUNG_MODULUS: definition({
    code: 'YOUNG_MODULUS', name: '杨氏模量', shortName: 'E', dimension: 'CQ', unit: 'GPa',
    description: '杨氏模量，表征岩石刚度。',
    engineeringMeaning: '杨氏模量过高过脆易复杂化，过低难形成缝网；采用区间评价。',
    direction: 'interval', requirement: 'required',
    mutualExclusionGroup: 'BRITTLENESS_INPUT_GROUP',
    dataSourceHints: ['动态/静态岩石力学解释'],
    aliases: PARAMETER_ALIASES.YOUNG_MODULUS,
    interval: { minAcceptable: 15, optimalMin: 25, optimalMax: 60, maxAcceptable: 90 },
  }),
  POISSON_RATIO: definition({
    code: 'POISSON_RATIO', name: '泊松比', shortName: 'PR', dimension: 'CQ', unit: '无量纲',
    description: '泊松比，表征岩石横向变形特性。',
    engineeringMeaning: '泊松比反映岩石脆韧性质，过低过脆、过高过韧；采用区间评价。',
    direction: 'interval', requirement: 'required',
    mutualExclusionGroup: 'BRITTLENESS_INPUT_GROUP',
    dataSourceHints: ['动态/静态岩石力学解释'],
    aliases: PARAMETER_ALIASES.POISSON_RATIO,
    interval: { minAcceptable: 0.10, optimalMin: 0.15, optimalMax: 0.28, maxAcceptable: 0.35 },
  }),
  HSTRESS_DIFF: definition({
    code: 'HSTRESS_DIFF', name: '水平主应力差', shortName: 'Δσh', dimension: 'CQ', unit: 'MPa',
    description: '最大与最小水平主应力之差。',
    engineeringMeaning: '应力差越大，缝网越难形成，负向指标；同时计入工程风险。',
    direction: 'negative', requirement: 'required', alternativeGroup: 'STRESS_GROUP',
    dataSourceHints: ['三向应力解释'],
    aliases: PARAMETER_ALIASES.HSTRESS_DIFF,
  }),
  FRACTURE_TOUGHNESS: definition({
    code: 'FRACTURE_TOUGHNESS', name: '断裂韧度', shortName: 'KIC', dimension: 'CQ', unit: 'MPa·m^0.5',
    description: '断裂韧度，表征岩石抵抗裂缝起裂与扩展的能力。',
    engineeringMeaning: '断裂韧度越高，起裂越困难，负向指标；同时计入工程风险。',
    direction: 'negative', requirement: 'recommended',
    dataSourceHints: ['岩石力学试验'],
    aliases: PARAMETER_ALIASES.FRACTURE_TOUGHNESS,
  }),
  NATURAL_FRACTURE: definition({
    code: 'NATURAL_FRACTURE', name: '天然裂缝指数', shortName: 'NF', dimension: 'CQ', unit: '无量纲',
    description: '天然裂缝发育程度（成像解释、裂缝密度或综合指标）。',
    engineeringMeaning: '适度天然裂缝有利于缝网形成，但过发育可能造成滤失；采用区间评价。',
    direction: 'interval', requirement: 'recommended', alternativeGroup: 'NATURAL_FRACTURE_GROUP',
    dataSourceHints: ['成像测井', '裂缝密度'],
    aliases: PARAMETER_ALIASES.NATURAL_FRACTURE,
    interval: { minAcceptable: 0.2, optimalMin: 0.5, optimalMax: 0.8, maxAcceptable: 1.0 },
  }),
  BARRIER_STRESS: definition({
    code: 'BARRIER_STRESS', name: '隔层应力差', shortName: 'Δσb', dimension: 'CQ', unit: 'MPa',
    description: '目标层与上下隔层的应力差，表征缝高约束能力。',
    engineeringMeaning: '隔层应力差越大，越利于将缝高约束在目标层内，正向指标。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['三向应力解释'],
    aliases: PARAMETER_ALIASES.BARRIER_STRESS,
  }),
  LAMINATION_INDEX: definition({
    code: 'LAMINATION_INDEX', name: '纹层/层理指数', shortName: 'LAM', dimension: 'CQ', unit: '无量纲',
    description: '纹层或层理发育程度指数。',
    engineeringMeaning: '适度的纹层/层理有利于裂缝扩展与复杂化；采用区间评价。',
    direction: 'interval', requirement: 'recommended',
    dataSourceHints: ['成像测井', '岩心描述'],
    aliases: PARAMETER_ALIASES.LAMINATION_INDEX,
    interval: { minAcceptable: 0.2, optimalMin: 0.5, optimalMax: 0.85, maxAcceptable: 1.0 },
  }),
  BARRIER_CONSTRAINT: definition({
    code: 'BARRIER_CONSTRAINT', name: '隔夹层约束能力', shortName: 'BAR', dimension: 'CQ', unit: '无量纲',
    description: '页岩油隔夹层对缝高与流体流动的约束能力。',
    engineeringMeaning: '约束能力越强，改造越集中，正向指标。',
    direction: 'positive', requirement: 'recommended',
    dataSourceHints: ['隔夹层解释'],
    aliases: PARAMETER_ALIASES.BARRIER_CONSTRAINT,
  }),
  TENSILE_STRENGTH: definition({
    code: 'TENSILE_STRENGTH', name: '抗拉强度', shortName: 'TSTR', dimension: 'CQ', unit: 'MPa',
    description: '岩石抗拉强度，表征起裂难易。',
    engineeringMeaning: '抗拉强度越高，起裂越困难，负向指标；同时计入工程风险。',
    direction: 'negative', requirement: 'recommended',
    dataSourceHints: ['岩石力学试验'],
    aliases: PARAMETER_ALIASES.TENSILE_STRENGTH,
  }),
  HETEROGENEITY: definition({
    code: 'HETEROGENEITY', name: '砾石—基质非均质指数', shortName: 'HET', dimension: 'CQ', unit: '无量纲',
    description: '砾岩中砾石与基质分布的非均质性。',
    engineeringMeaning: '强非均质会造成裂缝偏转与局部复杂化，采用区间评价；同时计入工程风险。',
    direction: 'interval', requirement: 'recommended',
    dataSourceHints: ['岩心/成像解释'],
    aliases: PARAMETER_ALIASES.HETEROGENEITY,
    interval: { minAcceptable: 0.1, optimalMin: 0.3, optimalMax: 0.7, maxAcceptable: 1.0 },
  }),
  COAL_STRUCTURE: definition({
    code: 'COAL_STRUCTURE', name: '煤体结构指数', shortName: 'CS', dimension: 'CQ', unit: '无量纲',
    description: '煤体原生结构完整程度（原生—碎裂—碎粒—糜棱）。',
    engineeringMeaning: '完整原生结构利于造缝与导流，过度破碎则不利；采用区间评价。',
    direction: 'interval', requirement: 'required',
    dataSourceHints: ['取心描述', '测井煤体结构解释'],
    aliases: PARAMETER_ALIASES.COAL_STRUCTURE,
    interval: { minAcceptable: 0.2, optimalMin: 0.5, optimalMax: 0.9, maxAcceptable: 1.0 },
  }),
  CLEAT_INDEX: definition({
    code: 'CLEAT_INDEX', name: '割理指数', shortName: 'CLI', dimension: 'CQ', unit: '无量纲',
    description: '割理发育程度指数。',
    engineeringMeaning: '适度割理有利于缝网形成，过发育易滤失；采用区间评价。',
    direction: 'interval', requirement: 'recommended',
    dataSourceHints: ['成像/岩心割理统计'],
    aliases: PARAMETER_ALIASES.CLEAT_INDEX,
    interval: { minAcceptable: 0.2, optimalMin: 0.5, optimalMax: 0.85, maxAcceptable: 1.0 },
  }),
  STRESS_SENSITIVITY: definition({
    code: 'STRESS_SENSITIVITY', name: '应力敏感性', shortName: 'SS', dimension: 'CQ', unit: '无量纲',
    description: '渗透率随有效应力变化而降低的程度。',
    engineeringMeaning: '应力敏感性越强，压后产能衰减风险越大，负向指标；同时计入工程风险。',
    direction: 'negative', requirement: 'recommended',
    dataSourceHints: ['应力敏感试验'],
    aliases: PARAMETER_ALIASES.STRESS_SENSITIVITY,
  }),
  SHMIN: definition({
    code: 'SHMIN', name: '最小水平主应力', shortName: 'Shmin', dimension: 'CQ', unit: 'MPa',
    description: '最小水平主应力。',
    engineeringMeaning: '影响起裂压力与缝高控制；与应力差联合解释，采用区间评价。',
    direction: 'interval', requirement: 'optional',
    dataSourceHints: ['三向应力解释'],
    aliases: PARAMETER_ALIASES.SHMIN,
  }),
  BI: definition({
    code: 'BI', name: '脆性指数', shortName: 'BI', dimension: 'CQ', unit: '%',
    description: '综合脆性指数（派生/综合参数，由杨氏模量＋泊松比＋脆性矿物含量或矿物法计算）。',
    engineeringMeaning: 'BI 为 CQ 综合指数，不得与基础参数组合（E＋PR＋脆性矿物）重复加权。',
    direction: 'positive', requirement: 'recommended', derived: true,
    derivedFrom: ['YOUNG_MODULUS', 'POISSON_RATIO', 'BRITTLE_MINERAL'],
    mutualExclusionGroup: 'BRITTLENESS_INPUT_GROUP',
    dataSourceHints: ['综合解释', '矿物法计算'],
    aliases: PARAMETER_ALIASES.BI,
  }),
  BRITTLE_MINERAL: definition({
    code: 'BRITTLE_MINERAL', name: '脆性矿物含量', shortName: 'BRIT_MIN', dimension: 'CQ', unit: '%',
    description: '石英、长石、碳酸盐等脆性矿物含量。',
    engineeringMeaning: '脆性矿物含量是脆性指数基础参数之一；与 BI 互斥。',
    direction: 'positive', requirement: 'optional',
    mutualExclusionGroup: 'BRITTLENESS_INPUT_GROUP',
    dataSourceHints: ['矿物解释'],
    aliases: PARAMETER_ALIASES.BRITTLE_MINERAL,
  }),
  CLAY_CONTENT: definition({
    code: 'CLAY_CONTENT', name: '黏土含量', shortName: 'VCL', dimension: 'CQ', unit: '%',
    description: '黏土矿物含量。',
    engineeringMeaning: '黏土含量过高降低脆性并增强水敏，负向指标。',
    direction: 'negative', requirement: 'optional',
    dataSourceHints: ['黏土矿物解释'],
    aliases: PARAMETER_ALIASES.CLAY_CONTENT,
  }),

  // ── 工程风险参数（不直接参与 RQ/CQ 正向得分） ──
  WATER_SENSITIVITY: definition({
    code: 'WATER_SENSITIVITY', name: '水敏性', shortName: 'WS', dimension: 'CQ', unit: '无量纲',
    description: '岩石遇水膨胀/分散导致渗透率降低的程度。',
    engineeringMeaning: '强水敏会造成储层伤害，属于风险参数，不直接作为 CQ 正向得分。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['水敏试验'],
    aliases: PARAMETER_ALIASES.WATER_SENSITIVITY,
  }),
  SEVERE_LOSS: definition({
    code: 'SEVERE_LOSS', name: '严重漏失风险', shortName: 'LOSS', dimension: 'CQ', unit: '无量纲',
    description: '天然裂缝/孔洞导致压裂液严重滤失的风险程度。',
    engineeringMeaning: '严重漏失会造成缝长不足，属于风险参数。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['滤失性评价'],
    aliases: PARAMETER_ALIASES.SEVERE_LOSS,
  }),
  PROPPANT_EMBEDMENT: definition({
    code: 'PROPPANT_EMBEDMENT', name: '支撑剂嵌入风险', shortName: 'EMB', dimension: 'CQ', unit: '无量纲',
    description: '支撑剂嵌入地层导致导流能力下降的风险。',
    engineeringMeaning: '嵌入风险高会降低裂缝导流，属于风险参数。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['嵌入试验'],
    aliases: PARAMETER_ALIASES.PROPPANT_EMBEDMENT,
  }),
  PROPPANT_CRUSHING: definition({
    code: 'PROPPANT_CRUSHING', name: '支撑剂破碎风险', shortName: 'CRUSH', dimension: 'CQ', unit: '无量纲',
    description: '高应力/强非均质下支撑剂破碎的风险。',
    engineeringMeaning: '破碎风险高会降低导流，属于风险参数。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['破碎率试验'],
    aliases: PARAMETER_ALIASES.PROPPANT_CRUSHING,
  }),
  HEIGHT_CONTROL_RISK: definition({
    code: 'HEIGHT_CONTROL_RISK', name: '缝高失控风险', shortName: 'HCTRL', dimension: 'CQ', unit: '无量纲',
    description: '隔层约束不足导致缝高失控的风险。',
    engineeringMeaning: '缝高失控会降低改造效率，属于风险参数。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['应力剖面解释'],
    aliases: PARAMETER_ALIASES.HEIGHT_CONTROL_RISK,
  }),
  SAND_PLUG_RISK: definition({
    code: 'SAND_PLUG_RISK', name: '砂堵风险', shortName: 'SPLUG', dimension: 'CQ', unit: '无量纲',
    description: '窄缝/低渗条件下加砂砂堵的风险。',
    engineeringMeaning: '砂堵风险高影响施工安全，属于风险参数。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['施工类比'],
    aliases: PARAMETER_ALIASES.SAND_PLUG_RISK,
  }),
  COAL_FRAGMENTATION_RISK: definition({
    code: 'COAL_FRAGMENTATION_RISK', name: '煤体破碎风险', shortName: 'CFR', dimension: 'CQ', unit: '无量纲',
    description: '煤岩破碎导致支撑剂嵌入与导流下降的风险。',
    engineeringMeaning: '煤体破碎风险高影响压后导流，属于风险参数。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['煤体结构评价'],
    aliases: PARAMETER_ALIASES.COAL_FRAGMENTATION_RISK,
  }),

  // ── 高级配置参数（不进入默认模板） ──
  RO: definition({
    code: 'RO', name: '镜质体反射率', shortName: 'Ro', dimension: 'RQ', unit: '%',
    description: '有机质成熟度。', engineeringMeaning: '用于评价热演化阶段，配合储层类型使用。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['有机地化测试'],
  }),
  MATRIX_PERM: definition({
    code: 'MATRIX_PERM', name: '基质渗透率', shortName: 'Km', dimension: 'RQ', unit: 'mD',
    description: '基质渗透率。', engineeringMeaning: '基质渗透率反映基质渗流能力。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['岩心渗透率'],
  }),
  BEDDING_DENSITY: definition({
    code: 'BEDDING_DENSITY', name: '层理密度', shortName: 'BD', dimension: 'CQ', unit: '条/m',
    description: '层理发育密度。', engineeringMeaning: '层理影响裂缝纵向扩展，配合储层类型使用。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['成像测井'],
  }),
  OIL_VISCOSITY: definition({
    code: 'OIL_VISCOSITY', name: '原油黏度', shortName: 'μo', dimension: 'RQ', unit: 'mPa·s',
    description: '原油黏度。', engineeringMeaning: '黏度过高会降低流动能力，负向影响产能。',
    direction: 'negative', requirement: 'optional',
    dataSourceHints: ['PVT 分析'],
  }),
  GOR: definition({
    code: 'GOR', name: '气油比', shortName: 'GOR', dimension: 'RQ', unit: 'm³/m³',
    description: '气油比。', engineeringMeaning: '气油比反映流体物性，配合储层类型使用。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['PVT 分析'],
  }),
  WETTABILITY: definition({
    code: 'WETTABILITY', name: '润湿性', shortName: 'WET', dimension: 'RQ', unit: '无量纲',
    description: '岩石润湿性。', engineeringMeaning: '润湿性影响油水分布与采出程度。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['润湿性试验'],
  }),
  EFFECTIVE_LITHOFACIES: definition({
    code: 'EFFECTIVE_LITHOFACIES', name: '有效岩相厚度', shortName: 'H_lith', dimension: 'RQ', unit: 'm',
    description: '有效岩相厚度。', engineeringMeaning: '有效岩相厚度表征有利岩相规模。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['岩相解释'],
  }),
  FLUID_MOBILITY: definition({
    code: 'FLUID_MOBILITY', name: '流体流动性', shortName: 'MOB', dimension: 'RQ', unit: '无量纲',
    description: '流体流动能力。', engineeringMeaning: '流动性反映产能潜力。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['NMR/试井'],
  }),
  UCS: definition({
    code: 'UCS', name: '单轴抗压强度', shortName: 'UCS', dimension: 'CQ', unit: 'MPa',
    description: '单轴抗压强度。', engineeringMeaning: 'UCS 影响裂缝起裂与扩展。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['岩石力学试验'],
  }),
  GRAVEL_CONTENT: definition({
    code: 'GRAVEL_CONTENT', name: '砾石含量', shortName: 'Vg', dimension: 'CQ', unit: '%',
    description: '砾石含量。', engineeringMeaning: '砾石含量影响非均质性与压裂行为。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['岩心/成像'],
  }),
  GRAVEL_DIAMETER: definition({
    code: 'GRAVEL_DIAMETER', name: '平均砾径', shortName: 'Dg', dimension: 'CQ', unit: 'mm',
    description: '平均砾径。', engineeringMeaning: '砾径影响裂缝偏转与滤失。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['岩心统计'],
  }),
  MATRIX_CONTENT: definition({
    code: 'MATRIX_CONTENT', name: '基质含量', shortName: 'Vm', dimension: 'CQ', unit: '%',
    description: '基质含量。', engineeringMeaning: '基质含量影响胶结与非均质。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['岩心统计'],
  }),
  GRAVEL_INTERFACE_DENSITY: definition({
    code: 'GRAVEL_INTERFACE_DENSITY', name: '砾石—基质界面密度', shortName: 'GI', dimension: 'CQ', unit: '条/m',
    description: '砾石—基质界面密度。', engineeringMeaning: '界面密度高易造成裂缝偏转与滤失，风险相关。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['岩心/成像'],
  }),
  FRACTURE_DEVIATION: definition({
    code: 'FRACTURE_DEVIATION', name: '裂缝偏转指数', shortName: 'FD', dimension: 'CQ', unit: '无量纲',
    description: '裂缝偏转程度。', engineeringMeaning: '偏转程度高降低缝网效率，风险相关。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['岩心/成像'],
  }),
  ROOF_FLOOR_STRESS: definition({
    code: 'ROOF_FLOOR_STRESS', name: '顶底板应力差', shortName: 'RF_STRESS', dimension: 'CQ', unit: 'MPa',
    description: '顶底板与煤层应力差。', engineeringMeaning: '应力差越大，缝高约束越好。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['应力解释'],
  }),
  ROOF_FLOOR_THICKNESS: definition({
    code: 'ROOF_FLOOR_THICKNESS', name: '顶底板厚度', shortName: 'RF_H', dimension: 'CQ', unit: 'm',
    description: '顶底板厚度。', engineeringMeaning: '顶底板厚度影响缝高控制。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['分层数据'],
  }),
  WATER_CONTENT: definition({
    code: 'WATER_CONTENT', name: '含水率', shortName: 'WC', dimension: 'RQ', unit: '%',
    description: '煤层含水率。', engineeringMeaning: '含水率影响解吸与排水。',
    direction: 'negative', requirement: 'optional',
    dataSourceHints: ['煤质分析'],
  }),
  HYDRO_WEAKENING: definition({
    code: 'HYDRO_WEAKENING', name: '水化弱化程度', shortName: 'HW', dimension: 'CQ', unit: '无量纲',
    description: '遇水弱化程度。', engineeringMeaning: '水化弱化影响强度与导流，风险相关。',
    direction: 'risk', requirement: 'optional',
    dataSourceHints: ['水化试验'],
  }),
  COAL_INTEGRITY: definition({
    code: 'COAL_INTEGRITY', name: '煤体完整程度', shortName: 'CI', dimension: 'CQ', unit: '无量纲',
    description: '煤体完整程度。', engineeringMeaning: '完整程度影响压裂可改造性。',
    direction: 'positive', requirement: 'optional',
    dataSourceHints: ['煤体结构解释'],
  }),
  ASH_CONTENT: definition({
    code: 'ASH_CONTENT', name: '灰分', shortName: 'ASH', dimension: 'RQ', unit: '%',
    description: '煤灰分。', engineeringMeaning: '灰分影响含气性与可压性。',
    direction: 'negative', requirement: 'optional',
    dataSourceHints: ['煤质分析'],
  }),
};

const RQ_TEMPLATE_DEFAULTS: Record<ReservoirType, string[]> = {
  'shale-gas': ['GAS_CONTENT', 'PHIE', 'SG', 'TOC', 'PRESSURE_COEF', 'NET_PAY'],
  'shale-oil': ['S1', 'PHIE', 'SO', 'TOC', 'MOVABLE_FLUID', 'PRESSURE_COEF'],
  'tight-sandstone-gas': ['PHIE', 'PERM', 'SG', 'NET_PAY', 'PRESSURE_COEF', 'MOVABLE_FLUID'],
  'tight-sandstone-oil': ['PHIE', 'PERM', 'SO', 'MOVABLE_FLUID', 'NET_PAY', 'PRESSURE_COEF'],
  'tight-conglomerate': ['PHIE', 'PERM', 'SO_OR_SG', 'MOVABLE_FLUID', 'NET_PAY', 'FACIES_CONTINUITY'],
  'coalbed-gas': ['GAS_CONTENT', 'SG', 'CLEAT_PERM', 'COAL_THICKNESS', 'DESORPTION_PRESSURE_RATIO', 'LANGMUIR_VOLUME'],
  custom: ['PHIE', 'PERM', 'SG', 'TOC', 'PRESSURE_COEF', 'NET_PAY'],
};

const CQ_TEMPLATE_DEFAULTS: Record<ReservoirType, string[]> = {
  'shale-gas': ['YOUNG_MODULUS', 'POISSON_RATIO', 'HSTRESS_DIFF', 'FRACTURE_TOUGHNESS', 'NATURAL_FRACTURE', 'BARRIER_STRESS'],
  'shale-oil': ['YOUNG_MODULUS', 'POISSON_RATIO', 'HSTRESS_DIFF', 'FRACTURE_TOUGHNESS', 'LAMINATION_INDEX', 'BARRIER_CONSTRAINT'],
  'tight-sandstone-gas': ['YOUNG_MODULUS', 'POISSON_RATIO', 'HSTRESS_DIFF', 'TENSILE_STRENGTH', 'FRACTURE_TOUGHNESS', 'NATURAL_FRACTURE'],
  'tight-sandstone-oil': ['YOUNG_MODULUS', 'POISSON_RATIO', 'HSTRESS_DIFF', 'FRACTURE_TOUGHNESS', 'NATURAL_FRACTURE', 'BARRIER_STRESS'],
  'tight-conglomerate': ['YOUNG_MODULUS', 'POISSON_RATIO', 'HSTRESS_DIFF', 'FRACTURE_TOUGHNESS', 'HETEROGENEITY', 'NATURAL_FRACTURE'],
  'coalbed-gas': ['COAL_STRUCTURE', 'YOUNG_MODULUS', 'POISSON_RATIO', 'HSTRESS_DIFF', 'CLEAT_INDEX', 'STRESS_SENSITIVITY'],
  custom: ['YOUNG_MODULUS', 'POISSON_RATIO', 'HSTRESS_DIFF', 'FRACTURE_TOUGHNESS', 'NATURAL_FRACTURE', 'BARRIER_STRESS'],
};

const RISK_DEFAULTS: Record<ReservoirType, string[]> = {
  'shale-gas': ['WATER_SENSITIVITY', 'SEVERE_LOSS', 'HEIGHT_CONTROL_RISK'],
  'shale-oil': ['WATER_SENSITIVITY', 'PROPPANT_EMBEDMENT', 'SAND_PLUG_RISK'],
  'tight-sandstone-gas': ['WATER_SENSITIVITY', 'SEVERE_LOSS', 'SAND_PLUG_RISK'],
  'tight-sandstone-oil': ['WATER_SENSITIVITY', 'PROPPANT_EMBEDMENT', 'HEIGHT_CONTROL_RISK'],
  'tight-conglomerate': ['HETEROGENEITY', 'PROPPANT_EMBEDMENT', 'PROPPANT_CRUSHING'],
  'coalbed-gas': ['COAL_FRAGMENTATION_RISK', 'WATER_SENSITIVITY', 'SAND_PLUG_RISK'],
  custom: [],
};

const ADVANCED_DEFAULTS: Record<ReservoirType, string[]> = {
  'shale-gas': ['RO', 'MATRIX_PERM', 'BRITTLE_MINERAL', 'CLAY_CONTENT', 'BEDDING_DENSITY', 'WATER_SENSITIVITY', 'SHMIN'],
  'shale-oil': ['RO', 'OIL_VISCOSITY', 'GOR', 'CLAY_CONTENT', 'NATURAL_FRACTURE', 'WATER_SENSITIVITY', 'EFFECTIVE_LITHOFACIES', 'FLUID_MOBILITY'],
  'tight-sandstone-gas': ['SHMIN', 'BARRIER_STRESS', 'CLAY_CONTENT', 'UCS', 'STRESS_SENSITIVITY', 'WATER_SENSITIVITY'],
  'tight-sandstone-oil': ['OIL_VISCOSITY', 'GOR', 'WETTABILITY', 'TENSILE_STRENGTH', 'STRESS_SENSITIVITY', 'WATER_SENSITIVITY', 'PROPPANT_EMBEDMENT'],
  'tight-conglomerate': ['GRAVEL_CONTENT', 'GRAVEL_DIAMETER', 'MATRIX_CONTENT', 'GRAVEL_INTERFACE_DENSITY', 'FRACTURE_DEVIATION', 'BARRIER_STRESS', 'PROPPANT_EMBEDMENT', 'PROPPANT_CRUSHING'],
  'coalbed-gas': ['ROOF_FLOOR_STRESS', 'ROOF_FLOOR_THICKNESS', 'TENSILE_STRENGTH', 'FRACTURE_TOUGHNESS', 'WATER_CONTENT', 'HYDRO_WEAKENING', 'COAL_INTEGRITY', 'ASH_CONTENT'],
  custom: ['SHMIN', 'CLAY_CONTENT', 'BRITTLE_MINERAL'],
};

const templateNotes: Record<ReservoirType, string[]> = {
  'shale-gas': ['海相/陆相页岩气水平井适用', '脆性信息建议使用综合脆性指数或基础参数组合，避免重复加权'],
  'shale-oil': ['页岩油以游离烃为主', 'S1＋TOC 与 OSI 只能选择一种'],
  'tight-sandstone-gas': ['致密砂岩气以孔渗为主', '渗透率评分前进行 log10 变换'],
  'tight-sandstone-oil': ['致密砂岩油以孔渗与可动流体为主', 'SO 与 SW 不得重复加权'],
  'tight-conglomerate': ['砾岩强非均质，重点关注砾石—基质界面', '支撑剂嵌入与破碎风险需单独评估'],
  'coalbed-gas': ['煤层气以割理系统为主', '煤体结构对可压性影响显著'],
  custom: ['由用户自定义参数与权重', '可在现有模板基础上复制修改'],
};

const buildTemplate = (type: ReservoirType): ReservoirQualityTemplate => ({
  id: type,
  name: RESERVOIR_TYPE_LABELS[type],
  version: RESERVOIR_TEMPLATE_VERSION,
  description: `${RESERVOIR_TYPE_LABELS[type]}评价模板：默认 ${RQ_TEMPLATE_DEFAULTS[type].length} 个 RQ 指标 + ${CQ_TEMPLATE_DEFAULTS[type].length} 个 CQ 指标，风险与高级参数单独维护。`,
  rqParameterCodes: RQ_TEMPLATE_DEFAULTS[type],
  cqParameterCodes: CQ_TEMPLATE_DEFAULTS[type],
  riskParameterCodes: RISK_DEFAULTS[type],
  optionalParameterCodes: ADVANCED_DEFAULTS[type],
  defaultMethod: 'legacy-rule',
  notes: templateNotes[type],
});

/** 储层评价模板（仅引用统一参数字典代码）。 */
export const RESERVOIR_TEMPLATES: Record<ReservoirType, ReservoirQualityTemplate> = Object.fromEntries(
  RESERVOIR_TYPES.map((type) => [type, buildTemplate(type)]),
) as Record<ReservoirType, ReservoirQualityTemplate>;

export function getReservoirTemplate(type: ReservoirType): ReservoirQualityTemplate {
  return RESERVOIR_TEMPLATES[type];
}

export function getParameterDefinition(code: string): SweetSpotParameterDefinition | undefined {
  return PARAMETER_DEFINITIONS[code];
}

/** 模板内参数定义列表（含风险与高级参数）。 */
export function parameterDefinitionsForTemplate(template: ReservoirQualityTemplate): SweetSpotParameterDefinition[] {
  const codes = [
    ...template.rqParameterCodes, ...template.cqParameterCodes,
    ...(template.riskParameterCodes ?? []), ...(template.optionalParameterCodes ?? []),
  ];
  return codes.map((code) => PARAMETER_DEFINITIONS[code]).filter(
    (definition): definition is SweetSpotParameterDefinition => Boolean(definition),
  );
}

const DIRECTION_TO_PARAMETER_DIRECTION: Record<ScoreDirection, 'larger_is_better' | 'smaller_is_better' | 'target_range'> = {
  positive: 'larger_is_better',
  negative: 'smaller_is_better',
  interval: 'target_range',
  risk: 'smaller_is_better',
};

const scoreDirectionOf = (direction: 'larger_is_better' | 'smaller_is_better' | 'target_range'): ScoreDirection => {
  if (direction === 'target_range') return 'interval';
  if (direction === 'smaller_is_better') return 'negative';
  return 'positive';
};

function parameterFromDefinition(
  definition: SweetSpotParameterDefinition,
  group: 'rq' | 'cq',
  index: number,
  groupSize: number,
): SweetSpotParameter {
  const enabled = definition.direction !== 'risk' && definition.code !== 'CLAY_CONTENT';
  return {
    id: `${group}.${definition.code.toLowerCase()}`,
    key: definition.code.toLowerCase() as SweetSpotParameter['key'],
    group,
    label: definition.name,
    unit: definition.unit ?? '无量纲',
    enabled,
    // 默认等权：每组 6 个默认参数权重均分，避免缺乏依据时编造权重。
    weight: Number((1 / Math.max(1, groupSize)).toFixed(4)),
    direction: DIRECTION_TO_PARAMETER_DIRECTION[definition.direction],
    targetMin: definition.interval?.optimalMin,
    targetMax: definition.interval?.optimalMax,
  };
}

/**
 * 将储层模板转换为页面可用的参数配置。
 * risk 方向参数默认不参与 RQ/CQ 正向得分（用于工程风险），保持 disabled。
 */
export function createParameterConfigFromTemplate(
  template: ReservoirQualityTemplate,
  existing?: SweetSpotParameterConfig,
): SweetSpotParameterConfig {
  const rq = template.rqParameterCodes
    .map((code) => PARAMETER_DEFINITIONS[code])
    .filter((definition): definition is SweetSpotParameterDefinition => Boolean(definition))
    .map((definition, index) => {
      const base = parameterFromDefinition(definition, 'rq', index, template.rqParameterCodes.length);
      return mergeWithExisting(base, existing?.rq);
    });
  const cq = template.cqParameterCodes
    .map((code) => PARAMETER_DEFINITIONS[code])
    .filter((definition): definition is SweetSpotParameterDefinition => Boolean(definition))
    .map((definition, index) => {
      const base = parameterFromDefinition(definition, 'cq', index, template.cqParameterCodes.length);
      return mergeWithExisting(base, existing?.cq);
    });
  return { rq, cq };
}

function mergeWithExisting(
  base: SweetSpotParameter,
  existing: SweetSpotParameter[] | undefined,
): SweetSpotParameter {
  if (!existing) return base;
  const saved = existing.find((item) => item.id === base.id);
  if (!saved) return base;
  return {
    ...base,
    enabled: typeof saved.enabled === 'boolean' ? saved.enabled : base.enabled,
    weight: typeof saved.weight === 'number' && Number.isFinite(saved.weight) && saved.weight >= 0 ? saved.weight : base.weight,
  };
}

/** 生成参数参与评价的运行时配置（含 DataHub 匹配状态）。 */
export function buildParameterEvaluationConfigs(
  template: ReservoirQualityTemplate,
  availableCurveMnemonics: string[],
  options?: { enabledCodes?: string[]; manualMappings?: Record<string, string> },
): Record<string, import('./types').ParameterEvaluationConfig> {
  const definitions = parameterDefinitionsForTemplate(template);
  const enabledCodes = options?.enabledCodes;
  const mnemonics = new Set(availableCurveMnemonics.map((item) => item.toUpperCase()));
  const output: Record<string, import('./types').ParameterEvaluationConfig> = {};
  definitions.forEach((definition) => {
    const manual = options?.manualMappings?.[definition.code];
    const matched = manual
      ?? definition.aliases?.find((alias) => mnemonics.has(alias.toUpperCase()) || [...mnemonics].some((m) => m.includes(alias.toUpperCase())));
    const enabled = enabledCodes
      ? enabledCodes.includes(definition.code)
      : definition.direction !== 'risk' && definition.code !== 'CLAY_CONTENT';
    output[definition.code] = {
      code: definition.code,
      enabled,
      weight: definition.defaultWeight ?? (enabled ? 1 / Math.max(1, definitions.filter((d) => d.dimension === definition.dimension && (enabledCodes
        ? enabledCodes.includes(d.code) : d.direction !== 'risk' && d.code !== 'CLAY_CONTENT')).length) : 0),
      requirement: definition.requirement,
      dataStatus: !enabled ? 'disabled' : definition.derived ? 'derived' : matched ? 'matched' : 'missing',
      direction: definition.direction,
      unit: definition.unit,
      matchedSourceCurve: matched,
      mappingSource: manual ? 'manual' : definition.derived ? 'derived' : matched ? 'datahub' : 'template',
      targetRange: definition.interval,
    };
  });
  return output;
}

export type ParameterConflict = {
  group: string;
  label: string;
  message: string;
  enabledCodes: string[];
};

/**
 * 重复加权/重复信息检查（数据层、算法层、界面层共用）。
 * 返回非空数组时，界面必须显示警告，算法层应避免静默重复加权。
 */
export function checkParameterConflicts(enabledCodes: string[]): ParameterConflict[] {
  const conflicts: ParameterConflict[] = [];
  (Object.keys(MUTUAL_EXCLUSION_GROUPS) as MutualExclusionKey[]).forEach((key) => {
    const group = MUTUAL_EXCLUSION_GROUPS[key];
    const enabledInGroup = enabledCodes.filter((code) => (
      [...group.basicMode, ...group.compositeMode] as readonly string[]
    ).includes(code));
    if (enabledInGroup.length < 2) return;
    const hasBasic = group.basicMode.some((code) => enabledInGroup.includes(code));
    const hasComposite = group.compositeMode.some((code) => enabledInGroup.includes(code));
    if (hasBasic && hasComposite) {
      conflicts.push({
        group: group.key,
        label: group.label,
        message: group.message,
        enabledCodes: enabledInGroup,
      });
    }
  });
  return conflicts;
}

/** 模板内重复加权风险检查：返回存在的互斥冲突。 */
export function checkTemplateConflicts(template: ReservoirQualityTemplate): ParameterConflict[] {
  return checkParameterConflicts([...template.rqParameterCodes, ...template.cqParameterCodes]);
}

/**
 * 参数完整度摘要。
 * required 缺失：默认阻止正常评价（允许工程师人工确认后进入降级模式，降低置信度）。
 */
export function computeCompletenessSummary(
  template: ReservoirQualityTemplate,
  evaluations: Record<string, import('./types').ParameterEvaluationConfig>,
) {
  const countByRequirement: Record<ParameterRequirement, { total: number; missing: number }> = {
    required: { total: 0, missing: 0 },
    recommended: { total: 0, missing: 0 },
    optional: { total: 0, missing: 0 },
  };
  const definitions = parameterDefinitionsForTemplate(template);
  definitions.forEach((definition) => {
    const evaluation = evaluations[definition.code];
    if (!evaluation || !evaluation.enabled) return;
    countByRequirement[definition.requirement].total += 1;
    if (evaluation.dataStatus === 'missing' || evaluation.dataStatus === 'invalid') {
      countByRequirement[definition.requirement].missing += 1;
    }
  });
  const allTotal = Object.values(countByRequirement).reduce((sum, item) => sum + item.total, 0);
  const allMissing = Object.values(countByRequirement).reduce((sum, item) => sum + item.missing, 0);
  return {
    ...countByRequirement,
    total: allTotal,
    missing: allMissing,
    completeness: allTotal ? (allTotal - allMissing) / allTotal : 0,
  };
}

/** 统一状态标签（集中维护，界面直接引用）。 */
export const PARAMETER_DATA_STATUS_LABELS: Record<import('./types').ParameterDataStatus, string> = {
  matched: '已匹配',
  candidate: '候选匹配',
  missing: '缺失',
  manual: '人工映射',
  alternative: '替代映射',
  derived: '派生计算',
  disabled: '未启用',
  invalid: '数据异常',
};

export const PARAMETER_REQUIREMENT_LABELS: Record<ParameterRequirement, string> = {
  required: '必选',
  recommended: '推荐',
  optional: '可选',
};

export function scoreDirectionLabel(direction: ScoreDirection): string {
  if (direction === 'positive') return '正向';
  if (direction === 'negative') return '负向';
  if (direction === 'interval') return '区间';
  return '风险';
}

/** 将现有参数方向映射为统一评分方向（用于展示与检查）。 */
export function toScoreDirection(
  direction: 'larger_is_better' | 'smaller_is_better' | 'target_range',
): ScoreDirection {
  return scoreDirectionOf(direction);
}
